import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Link } from 'expo-router';
import { MaterialIcons } from '@expo/vector-icons';
import { DigestContextBannerProps } from './types';
import { getDigestUrl } from '../../lib/paperUrl';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interSemiBold, interRegular } from '../../lib/fonts';
import RelevanceBadge from './RelevanceBadge';
import { Trans } from '@lingui/react/macro';


/**
 * DigestContextBanner shows when a paper is viewed within a digest issue
 * Displays digest name, rank position, relevance score, and relevance class
 */
export default function DigestContextBanner({
    digestEntryContext,
    projectPaperContext,
}: DigestContextBannerProps) {
    const { fontsLoaded } = useFontLoading();

    const { digestName, digestId, rankPosition, totalPapers } = digestEntryContext;
    const { relevanceScore, relevanceClass } = projectPaperContext;
    const digestUrl = getDigestUrl(digestId, digestName);

    return (
        <View style={styles.container}>
            <View style={styles.headerRow}>
                <MaterialIcons name="collections-bookmark" size={20} color={colors.primaryGreen} />
                <Text style={[styles.label, fontsLoaded && interRegular]}><Trans id="papers.digestcontextbanner.fromDigest" comment="UI copy in components/papers/DigestContextBanner.tsx">
                    From digest
                </Trans></Text>
                <Link href={digestUrl} asChild>
                    <TouchableOpacity activeOpacity={0.7}>
                        <Text style={[styles.digestName, fontsLoaded && interSemiBold]}>
                            {digestName}
                        </Text>
                    </TouchableOpacity>
                </Link>
            </View>

            <View style={styles.statsRow}>
                {rankPosition && (
                    <View style={styles.statItem}>
                        <MaterialIcons name="format-list-numbered" size={16} color={colors.text700} />
                        <Text style={[styles.statText, fontsLoaded && interRegular]}><Trans id="papers.digestcontextbanner.ranked" comment="UI copy in components/papers/DigestContextBanner.tsx">
                            Ranked #{rankPosition}
                            {totalPapers && ` of ${totalPapers}`}
                        </Trans></Text>
                    </View>
                )}

                {relevanceClass !== null && relevanceClass !== undefined && (
                    <RelevanceBadge relevanceClass={relevanceClass} />
                )}

                {relevanceScore !== null && relevanceScore !== undefined && (
                    <View style={styles.statItem}>
                        <MaterialIcons name="search" size={16} color={colors.text700} />
                        <Text style={[styles.statText, fontsLoaded && interRegular]}><Trans id="papers.digestcontextbanner.relevance" comment="UI copy in components/papers/DigestContextBanner.tsx">
                            Relevance: {Math.round(relevanceScore * 100)}%
                        </Trans></Text>
                    </View>
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        gap: 12,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
    },
    label: {
        fontSize: 12,
        color: colors.text700,
        textTransform: 'uppercase',
        letterSpacing: 0.35,
    },
    digestName: {
        fontSize: 13,
        color: colors.primaryGreenDark,
        fontWeight: '600',
    },
    statsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
    },
    statItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 999,
        backgroundColor: '#f1f5f9',
    },
    statText: {
        fontSize: 12,
        color: colors.text700,
    },
});
